import { fitText } from "@/lib/fit-title";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { absoluteUrl } from "@/config/sites";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnswerFirst } from "@/components/AnswerFirst";
import { WP_REVIEWED_ISO, WP_REVIEWED_TH, WP_TOPIC_BY_SLUG } from "@/data/workpermit";
import { WP_QA_ALL } from "@/data/workpermit/questions";
import { JsonLd } from "@/components/JsonLd";

export const Route = createFileRoute("/work-permit/topic/$topic")({
  loader: ({ params }) => {
    if (!WP_TOPIC_BY_SLUG[params.topic]) throw notFound();
    return { topic: params.topic };
  },
  head: ({ loaderData }) => {
    const t = loaderData ? WP_TOPIC_BY_SLUG[loaderData.topic] : undefined;
    if (!t) {
      return { meta: [{ title: ctrTitle("ไม่พบหน้า") }, { name: "robots", content: "noindex" }] };
    }
    const url = absoluteUrl(`/work-permit/topic/${t.slug}`);
    const title = fitText(`${t.th} | Thai Notary Law`, 60);
    const desc = fitText(t.summaryTh, 158);
    return {
      meta: [
        { title: ctrTitle(title) },
        { name: "description", content: ctrDesc(desc) },
        { property: "og:title", content: ctrTitle(title) },
        { property: "og:description", content: ctrDesc(desc) },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl p-8 text-center">
      <h1 className="mb-2 text-xl font-bold">ไม่พบหัวข้อนี้</h1>
      <Link to="/work-permit" className="text-blue-700 hover:underline">
        กลับหน้ารวมบริการ →
      </Link>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-2xl p-8 text-center text-sm">
      โหลดหน้านี้ไม่สำเร็จ กรุณารีเฟรชอีกครั้ง
    </div>
  ),
  component: WpTopicPage,
});

function WpTopicPage() {
  const { topic } = Route.useLoaderData();
  const t = WP_TOPIC_BY_SLUG[topic];
  if (!t) throw notFound();
  const url = absoluteUrl(`/work-permit/topic/${t.slug}`);
  const faqs = WP_QA_ALL.filter((q) => q.topicSlug === t.slug).slice(0, 8);

  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${url}#page`,
        url,
        name: t.th,
        description: t.summaryTh,
        dateModified: WP_REVIEWED_ISO,
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: [".answer-summary", ".faq-answer"],
        },
      },
      {
        "@type": "HowTo",
        name: `ขั้นตอน${t.th}`,
        step: t.stepsTh.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title,
          text: s.detail,
        })),
      },
      ...(faqs.length
        ? [
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((q) => ({
                "@type": "Question",
                name: q.q,
                acceptedAnswer: { "@type": "Answer", text: q.a },
              })),
            },
          ]
        : []),
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "หน้าแรก", item: absoluteUrl("/") },
          {
            "@type": "ListItem",
            position: 2,
            name: "ใบอนุญาตทำงานและงานตรวจคนเข้าเมือง",
            item: absoluteUrl("/work-permit"),
          },
          { "@type": "ListItem", position: 3, name: t.th, item: url },
        ],
      },
    ],
  };

  return (
    <>
      <JsonLd data={graph} />
      <Breadcrumbs
        items={[
          { label: "หน้าแรก", to: "/" },
          { label: "ใบอนุญาตทำงานและงานตรวจคนเข้าเมือง", to: "/work-permit" },
          { label: t.th },
        ]}
      />
      <section className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="mb-4 text-2xl font-bold">{t.th}</h1>
        <AnswerFirst>{t.summaryTh}</AnswerFirst>

        <h2 className="mb-2 mt-8 text-xl font-semibold">ข้อเท็จจริงสำคัญ</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {t.factsTh.map((f) => (
                <tr key={f.label} className="border-b align-top">
                  <th scope="row" className="w-2/5 py-2 pr-3 text-left font-medium">
                    {f.label}
                  </th>
                  <td className="py-2">{f.value}</td>
                </tr>
              ))}
              <tr className="border-b align-top">
                <th scope="row" className="py-2 pr-3 text-left font-medium">
                  ช่องทางยื่น
                </th>
                <td className="py-2">{t.channelsTh.join(" · ")}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="mb-2 mt-8 text-xl font-semibold">เอกสารที่ต้องเตรียม</h2>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {t.requirementsTh.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>

        <h2 className="mb-2 mt-8 text-xl font-semibold">ขั้นตอนดำเนินการ</h2>
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          {t.stepsTh.map((s) => (
            <li key={s.title}>
              <span className="font-medium">{s.title}</span> — {s.detail}
            </li>
          ))}
        </ol>

        <h2 className="mb-2 mt-8 text-xl font-semibold">คำแนะนำก่อนยื่น</h2>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {t.tipsTh.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>

        <h2 className="mb-2 mt-8 text-xl font-semibold">ข้อควรระวัง / สาเหตุถูกตีกลับ</h2>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {t.warningsTh.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>

        {faqs.length > 0 && (
          <>
            <h2 className="mb-2 mt-8 text-xl font-semibold">คำถามที่พบบ่อย</h2>
            <dl className="space-y-3 text-sm">
              {faqs.map((q) => (
                <div key={q.slug}>
                  <dt className="font-medium">
                    <Link
                      to="/work-permit/q/$slug"
                      params={{ slug: q.slug }}
                      className="text-blue-700 hover:underline"
                    >
                      {q.q}
                    </Link>
                  </dt>
                  <dd className="faq-answer mt-1 leading-relaxed">{q.a}</dd>
                </div>
              ))}
            </dl>
          </>
        )}

        <p className="mt-8 text-xs text-muted-foreground">
          {t.citationTh} · ข้อมูล ณ {WP_REVIEWED_TH} · ค่าบริการแจ้งเป็นรายเคส สอบถามทาง{" "}
          <Link to="/contact" className="underline">
            โทรศัพท์ / LINE / อีเมล
          </Link>
        </p>
      </section>
    </>
  );
}
